import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import { COMPANY_FLAG_API_SERVICE_TOKEN, WATCHLIST_API_SERVICE_TOKEN } from '@nk/api-lib';
import {
  AppIdentityCompositeEntity,
  CompanyEntity,
  PartialCompanyByUuid,
  WatchlistEntity,
  WithUuid,
} from '@nk/data-entities-lib';
import { ButtonDirective, NkToastrService } from '@nk/ui-lib';
import { arrayUnique } from '@nk/utils-lib';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { EMPTY, Observable, catchError, of, switchMap } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';

@Component({
  selector: 'nk-watchlist-create-modal',
  imports: [ReactiveFormsModule, TranslateModule, ModalModule, ButtonDirective],
  templateUrl: './watchlist-create-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WatchlistCreateModalComponent implements OnInit {
  // Input param
  public companyUuidList: string[] = [];

  // Signals for state management
  public createWatchlistDisabled = signal(true);
  public identity = signal<AppIdentityCompositeEntity | null>(null);
  public showErrorMessage = signal(false);
  /**
   * output: the created watchlist
   */
  public createdWatchlist = signal<WithUuid<WatchlistEntity> | null>(null);

  // Computed values
  public isPersonal = computed(() => this.watchlistForm.get('personal')?.value === true);

  // Form using typed FormGroup
  public watchlistForm = new FormGroup({
    name: new FormControl<string | null>(null, Validators.required),
    personal: new FormControl<boolean | null>(null, Validators.required),
  });
  private _watchlistService = inject(WATCHLIST_API_SERVICE_TOKEN);
  private _companyFlagService = inject(COMPANY_FLAG_API_SERVICE_TOKEN);

  private _appService = inject(AppService);
  private _toastr = inject(NkToastrService);
  private _destroy = inject(DestroyRef);
  private _translateService = inject(TranslateService);
  private _bsModalRef = inject(BsModalRef);

  public ngOnInit(): void {
    this._appService.identityDataCache
      .get()
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe((identity) => {
        this.createWatchlistDisabled.set(false);
        this.identity.set(identity);
      });
  }

  public onValidateCreateWatchlist(): void {
    if (this.watchlistForm.valid && !this.createWatchlistDisabled()) {
      this.createWatchlistDisabled.set(true);
      this.onCreateWatchlist();
    } else {
      this.showErrorMessage.set(true);
    }
  }

  public onCreateWatchlist(): void {
    const identity = this.identity();
    if (!identity) {
      this._handleError(
        extractTranslation('watchlist_wizard.unexpected_happened_create'),
        extractTranslation('watchlist_wizard.unexpected_happened_create_title'),
      );
      return;
    }

    const name = this.watchlistForm.controls['name'].value;
    const personalFlag = this.watchlistForm.controls['personal'].value;

    if (!name || personalFlag === null) {
      this.showErrorMessage.set(true);
      return;
    }

    const partialWatchlist: WatchlistEntity = {
      app_customer_uuid: identity.app_customer.uuid,
      app_membership_uuid: identity.app_membership.uuid,
      name,
      personal_flag: personalFlag,
    };

    this._watchlistService
      .createWatchlist(partialWatchlist)
      .pipe(
        tap((watchlist) => {
          this.createdWatchlist.set(watchlist);
        }),
        switchMap((watchlist) => this._addCompaniesToWatchlist(watchlist)),
        takeUntilDestroyed(this._destroy),
      )
      .subscribe({
        next: () => {
          this._watchlistService.watchlistListByCustomerCache.invalidateAllCache();
          this._watchlistService.watchlistByUuidCache.invalidateAllCache();
          this._companyFlagService.companyFlagDashboardCompanyCompositeCollectionCache.invalidateAllCache();
          this.showSuccessMessage();
          this._bsModalRef.hide();
        },
        error: () => {
          this._handleError(
            extractTranslation('watchlist_wizard.unexpected_happened_create'),
            extractTranslation('watchlist_wizard.unexpected_happened_create_title'),
          );
        },
      });
  }

  private _addCompaniesToWatchlist(watchlist: WithUuid<WatchlistEntity>): Observable<WithUuid<WatchlistEntity>> {
    const companyUuids = arrayUnique(this.companyUuidList);

    if (companyUuids.length === 0) {
      return of(watchlist);
    }

    if (companyUuids.length === 1) {
      const partialCompany = { uuid: companyUuids[0] } as PartialCompanyByUuid;
      return this._watchlistService.postCompanyToWatchlist(watchlist.uuid, partialCompany, false).pipe(
        first(),
        map(() => watchlist),
        catchError(() => {
          this._handleError(
            extractTranslation('watchlist_wizard.unexpected_happened_add'),
            extractTranslation('watchlist_wizard.unexpected_happened_add_title'),
          );
          return EMPTY;
        }),
      );
    } else {
      const partialCompanies = companyUuids.map(
        (companyUuid) =>
          ({
            company_uuid: companyUuid,
          }) as Partial<CompanyEntity>,
      );

      return this._watchlistService.postCompaniesToWatchlist(watchlist.uuid, partialCompanies).pipe(
        first(),
        map(() => watchlist),
        catchError(() => {
          this._handleError(
            extractTranslation('watchlist_wizard.unexpected_happened_add'),
            extractTranslation('watchlist_wizard.unexpected_happened_add_title'),
          );
          return EMPTY;
        }),
      );
    }
  }

  private _handleError(descriptionKey: string, titleKey: string): void {
    const description = this._translateService.instant(descriptionKey);
    const title = this._translateService.instant(titleKey);
    this._toastr.error(description, title);
  }
  public showSuccessMessage(): void {
    const watchlist = this.createdWatchlist();
    if (!watchlist) return;

    const title = this._translateService.instant(extractTranslation('watchlist_wizard.success_title'), {
      name: watchlist.name,
    });
    const description = this._translateService.instant(extractTranslation('watchlist_wizard.success_description'));
    this._toastr.success(description, title);
  }

  public cancel(): void {
    this._bsModalRef.hide();
  }
}
